/**
 * Mobile header hide-on-scroll.
 *
 * Scrolling down hides the sticky header; scrolling back up brings it straight
 * back. It is a direction toggle, not a timer - once hidden it stays hidden
 * until the reader actually scrolls up, including when they stop dead.
 *
 * Mobile only. On desktop there is room for the header and the sticky nav is
 * the point of it, so this listener does nothing unless the media query
 * matches. Everything visual lives in the header section's own stylesheet;
 * this file only owns the "is it hidden" bit.
 *
 * The header itself is not touched when it isn't sticky - a header that
 * scrolls away on its own has nothing to hide.
 */
(() => {
  let header = document.querySelector('[data-header-scroll-hide]');
  if (!header) return;

  const mobile = window.matchMedia('(max-width: 767px)');

  // Below this, a scroll is a thumb tremor or a rubber-band settle rather than
  // an intent to move, and reacting to it makes the header flicker. Low enough
  // that the first frame of a real gesture is already past it - a deliberate
  // flick covers this in a single event.
  const THRESHOLD = 4;

  // How much vertical space the header group occupies in normal flow - the
  // announcement bar plus the header itself. Sticky positioning keeps that
  // space reserved, so until the page has scrolled past it the content below
  // has not yet reached the top of the viewport.
  //
  // This is the whole reason the hiding is gated. Pull the header out early and
  // what gets revealed is that reserved space, which is empty and paints as the
  // page background - the white band. Past this point the header is genuinely
  // overlaying content, so taking it away reveals the image underneath.
  //
  // offsetHeight is used rather than offsetTop because a sticky element reports
  // its shifted position, not its flow position.
  const groupHeight = () =>
    [...document.querySelectorAll('.shopify-section-group-header-group')].reduce(
      (total, el) => total + el.offsetHeight,
      0
    );

  // Left at 0 until start() measures it. Computing it up front would force a
  // layout read on desktop for a number desktop never uses.
  let overlapsAt = 0;
  let lastY = window.scrollY;
  let ticking = false;

  /* The header carries two independent states.

     .header-hidden is the transform - is it on screen or not.

     .header-opaque is the colour - transparent with reversed-out marks over
     the hero, or the normal white bar. They are separate because the colour
     must NOT change while the header is still visible. Swapping it as soon as
     the page leaves the top made the bar flash white and then slide away, and
     swapping it at the same moment as the transform was no better: the slide
     takes 200ms, so a white header was still visible travelling upward.

     So the colour flips only once the header is genuinely off screen, on the
     transitionend of the hide. Going down it leaves still transparent; coming
     back it is already white before the first pixel of it is on screen. */
  const show = () => header?.classList.remove('header-hidden');

  const hide = () => {
    if (!header) return;
    if (header.classList.contains('header-hidden')) return;
    header.classList.add('header-hidden');

    // prefers-reduced-motion kills the transition, so transitionend never
    // fires. There is no travel to be seen in that case, so flip immediately.
    if (getComputedStyle(header).transitionDuration === '0s') {
      header.classList.add('header-opaque');
      return;
    }

    header.addEventListener(
      'transitionend',
      (event) => {
        if (event.propertyName !== 'transform') return;
        // It may have been brought straight back mid-slide.
        if (header.classList.contains('header-hidden')) {
          header.classList.add('header-opaque');
        }
      },
      { once: true }
    );
  };

  const update = () => {
    ticking = false;

    // iOS overscroll reports negative offsets and offsets past the end of the
    // document; clamping keeps those from registering as a direction change.
    const y = Math.max(
      0,
      Math.min(
        window.scrollY,
        document.documentElement.scrollHeight - window.innerHeight
      )
    );
    const delta = y - lastY;

    if (Math.abs(delta) < THRESHOLD) return;

    // Nothing to gain from hiding it before it overlaps anything: over that
    // stretch the header is already travelling up with the page under its own
    // steam, so it reads as leaving the instant the scroll starts either way.
    if (y < overlapsAt) {
      // Back over the hero, where the header belongs to the image again.
      show();
      header?.classList.remove('header-opaque');
    } else if (delta < 0) {
      show();
    } else {
      hide();
    }

    lastY = y;
  };

  const onScroll = () => {
    if (ticking) return;
    ticking = true;
    window.requestAnimationFrame(update);
  };

  const start = () => {
    lastY = window.scrollY;
    overlapsAt = groupHeight();
    window.addEventListener('scroll', onScroll, { passive: true });
  };

  const stop = () => {
    window.removeEventListener('scroll', onScroll);
    // Leaving either class on would strand the header off-screen on desktop, or
    // leave it painted for a state desktop never enters.
    show();
    header?.classList.remove('header-opaque');
  };

  const sync = () => (mobile.matches ? start() : stop());

  sync();
  mobile.addEventListener('change', () => {
    stop();
    sync();
  });

  // The announcement bar wraps to a second line on a narrow screen, and the
  // header grows with it, so the measurement cannot be taken once and kept.
  // Gated: on desktop this would be a forced layout read on every resize event
  // for a number that is never consulted.
  window.addEventListener('resize', () => {
    if (!mobile.matches) return;
    overlapsAt = groupHeight();
  });

  // The theme editor re-renders sections in place, which swaps the header for a
  // brand new element. Re-query rather than reuse: the old node is detached by
  // this point, so anything still holding it would be styling nothing.
  document.addEventListener('shopify:section:load', (event) => {
    const replacement = event.target.querySelector('[data-header-scroll-hide]');
    if (!replacement) return;
    stop();
    header = replacement;
    sync();
  });
})();
